import type { NewsListBlockProps } from "./types";

import {
  extractYearMonth,
  getAvailableYears,
  getAvailableMonths,
  formatMonth,
} from "@/utils/date";
import Link from "@/components/link";
import { NewsArticleCard } from "@/components/blocks/news-article-card";
import { useState, useMemo, useEffect } from "react";
import {
  Field,
  Heading,
  Pagination,
  Select,
  usePagination,
} from "@digdir/designsystemet-react";
import { ArrowRightIcon } from "@navikt/aksel-icons";

export type { NewsListBlockProps };

export const NewsListBlock = ({
  blockHeading,
  newsPages,
  pageSize,
  disablePaging,
  disableFiltering,
  showArchiveButton,
  archiveUrl,
}: NewsListBlockProps) => {
  // Track screensize to adjust number of pages shown in pagination
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => setIsSmallScreen(window.innerWidth < 768);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Filtering
  const dates = newsPages.map((n) => n.published);
  const years = getAvailableYears(dates);
  const [selectedYear, setSelectedYear] = useState<number | "">(years[0] ?? "");
  const months =
    selectedYear !== "" ? getAvailableMonths(dates, selectedYear) : [];
  const [selectedMonth, setSelectedMonth] = useState<number | "">("");

  const filtered = useMemo(() => {
    if (!selectedYear && !selectedMonth) return newsPages;
    return newsPages.filter((n) => {
      const { year, month } = extractYearMonth(n.published);
      if (selectedYear && year !== selectedYear) return false;
      if (selectedMonth && month !== selectedMonth) return false;
      return true;
    });
  }, [newsPages, selectedYear, selectedMonth]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const total = filtered.length;
  const totalPages = disablePaging
    ? 1
    : Math.max(1, Math.ceil(total / pageSize));
  const pagesShown = Math.min(isSmallScreen ? 3 : 7, totalPages);

  const { pages, prevButtonProps, nextButtonProps, hasNext, hasPrev } =
    usePagination({
      currentPage,
      setCurrentPage,
      totalPages: totalPages,
      showPages: pagesShown,
    });

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage, setCurrentPage]);

  // Newsarticles shown at each page
  const articles = useMemo(() => {
    if (disablePaging) return filtered;
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize, disablePaging]);

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 justify-between md:flex-row">
        {blockHeading && (
          <Heading className="heading-md mb-0">{blockHeading}</Heading>
        )}

        {/* Filtering */}
        {!disableFiltering && (
          <div className="flex flex-col gap-2 md:flex-row md:ml-auto">
            <Field>
              <Select
                value={selectedYear}
                aria-label="Velg år"
                onChange={(e) => {
                  setSelectedYear(Number(e.target.value) || "");
                  setSelectedMonth("");
                }}
                className="h-10.5 py-1 border border-neutralNsf-subtle md:h-12 xl:h-13"
              >
                <Select.Option value="" className="text-md">
                  Alle år
                </Select.Option>
                {years.map((year) => (
                  <Select.Option key={year} value={year} className="text-md">
                    {year}
                  </Select.Option>
                ))}
              </Select>
            </Field>

            <Field>
              <Select
                value={selectedMonth}
                aria-label="Velg år"
                onChange={(e) => setSelectedMonth(Number(e.target.value) || "")}
                disabled={!selectedYear}
                className="h-10.5 py-1 border border-neutralNsf-subtle md:h-12 xl:h-13"
              >
                <Select.Option value="" className="text-md">
                  Alle måneder
                </Select.Option>
                {months.map((m) => (
                  <Select.Option key={m} value={m} className="text-md">
                    {formatMonth(m)}
                  </Select.Option>
                ))}
              </Select>
            </Field>
          </div>
        )}
      </div>

      {/* News article list */}
      <div className="grid justify-start gap-5 lg:grid-cols-3">
        {articles.map((article) => (
          <NewsArticleCard
            key={`${article.heading}-${article.published}`}
            url={article.url}
            heading={article.heading}
            introText={article.mainIntro}
            imageUrl={article.imageUrl}
            imgAltText={article.imgAltText}
          />
        ))}
      </div>

      {/* Pagination */}
      {!disablePaging && (
        <Pagination className="pagination">
          <Pagination.List className="justify-center">
            <Pagination.Item>
              <Pagination.Button
                aria-label="Forrige side"
                disabled={!hasPrev}
                className="px-2 md:px-4"
                {...prevButtonProps}
              >
                <span className="hidden md:inline">Forrige</span>
              </Pagination.Button>
            </Pagination.Item>

            {pages.map(({ page, itemKey, buttonProps }) => {
              if (page === "ellipsis") {
                return <Pagination.Item key={itemKey} />;
              }
              if (typeof page === "number" && page > 0 && page <= totalPages) {
                return (
                  <Pagination.Item key={itemKey}>
                    <Pagination.Button
                      asChild
                      aria-label={`Side ${page}`}
                      {...buttonProps}
                    >
                      <span>{page}</span>
                    </Pagination.Button>
                  </Pagination.Item>
                );
              }
              return null;
            })}

            <Pagination.Item>
              <Pagination.Button
                aria-label="Neste side"
                disabled={!hasNext}
                className="px-2 md:px-4"
                {...nextButtonProps}
              >
                <span className="hidden md:inline">Neste</span>
              </Pagination.Button>
            </Pagination.Item>
          </Pagination.List>
        </Pagination>
      )}

      {/* Link to newsarchive page */}
      {showArchiveButton && archiveUrl && (
        <Link href={archiveUrl} className="flex justify-end w-fit ml-auto">
          Se flere nyheter
          <ArrowRightIcon />
        </Link>
      )}
    </section>
  );
};
